import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { BUSINESS_CATEGORIES } from '@/lib/categories';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Store, ArrowLeft, ArrowRight, Check, Mail, Lock, CheckCircle2, XCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { validateItalianIBAN, normalizeIBAN } from '@/lib/iban';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RestaurantAuth() {
  const { t } = useLang();
  const { completeAuth, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('register'); // 'register' or 'login'
  const [step, setStep] = useState(1); // Step 1: email+password, Step 2: profile
  const [loading, setLoading] = useState(false);

  // Step 1 fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Step 2 fields
  const [companyName, setCompanyName] = useState('');
  const [registeredAddress, setRegisteredAddress] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [iban, setIban] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [category, setCategory] = useState('');
  const [ibanStatus, setIbanStatus] = useState({ valid: null, messageKey: null });
  // Legal representative fields
  const [legalRepFirstName, setLegalRepFirstName] = useState('');
  const [legalRepLastName, setLegalRepLastName] = useState('');
  const [legalRepPlaceOfBirth, setLegalRepPlaceOfBirth] = useState('');
  const [legalRepDateOfBirth, setLegalRepDateOfBirth] = useState('');
  const [legalRepFiscalCode, setLegalRepFiscalCode] = useState('');
  const [legalRepDocumentType, setLegalRepDocumentType] = useState('');
  const [legalRepDocumentNumber, setLegalRepDocumentNumber] = useState('');
  const [profileErrors, setProfileErrors] = useState({});

  // Step 1: Register or Login
  const handleStep1 = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email e password sono obbligatori');
      return;
    }
    if (mode === 'register' && password.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/auth/business/register' : '/auth/business/login';
      const res = await axios.post(`${API}${endpoint}`, { email, password }, { withCredentials: true });
      completeAuth(res.data);
      if (mode === 'register') {
        // After registration, go to step 2
        setStep(2);
        toast.success(t('cp_toast_created'));
      } else {
        // Login: check if profile is complete
        if (res.data.is_profile_complete) {
          navigate('/restaurant/dashboard', { replace: true });
        } else {
          setStep(2);
          toast.success('Bentornato! Completa il profilo per accedere a tutte le funzionalità.');
        }
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Defensive recovery: if we tried to register with an email that already exists,
      // silently try to log in with the same credentials (handles duplicate-submit and
      // partial-failure edge cases).
      if (mode === 'register' && status === 409) {
        try {
          const loginRes = await axios.post(`${API}/auth/business/login`, { email, password }, { withCredentials: true });
          completeAuth(loginRes.data);
          if (loginRes.data.is_profile_complete) {
            navigate('/restaurant/dashboard', { replace: true });
          } else {
            setStep(2);
            toast.success('Bentornato! Completa il profilo per accedere a tutte le funzionalità.');
          }
          return;
        } catch (loginErr) {
          const loginDetail = loginErr.response?.data?.detail;
          toast.error(typeof loginDetail === 'string' ? loginDetail : 'Email già registrata. Usa "Accedi" con la password corretta.');
          return;
        }
      }
      toast.error(typeof detail === 'string' ? detail : 'Errore');
    } finally { setLoading(false); }
  };

  // Step 2: Complete Profile
  const validateProfile = () => {
    const errors = {};
    if (!companyName.trim()) errors.companyName = 'Ragione sociale obbligatoria';
    if (!registeredAddress.trim()) errors.registeredAddress = 'Indirizzo obbligatorio';
    
    const vatClean = vatNumber.replace(/\s/g, '').replace(/^IT/i, '');
    if (!vatClean || !/^\d{11}$/.test(vatClean)) errors.vatNumber = 'Partita IVA: 11 cifre numeriche';
    
    const ibanCheck = validateItalianIBAN(iban);
    if (!ibanCheck.valid) errors.iban = t(ibanCheck.messageKey);
    
    const phoneClean = phoneNumber.replace(/[\s\-]/g, '');
    if (phoneClean.length < 8 || !/^\+?\d+$/.test(phoneClean)) errors.phoneNumber = 'Numero di telefono non valido';
    
    if (!category) errors.category = 'Seleziona una categoria';
    
    // Legal representative
    if (!legalRepFirstName.trim()) errors.legalRepFirstName = 'Nome obbligatorio';
    if (!legalRepLastName.trim()) errors.legalRepLastName = 'Cognome obbligatorio';
    if (!legalRepPlaceOfBirth.trim()) errors.legalRepPlaceOfBirth = 'Luogo di nascita obbligatorio';
    if (!legalRepDateOfBirth || !/^\d{4}-\d{2}-\d{2}$/.test(legalRepDateOfBirth)) errors.legalRepDateOfBirth = 'Data di nascita obbligatoria';
    if (legalRepFiscalCode.trim().length !== 16) errors.legalRepFiscalCode = 'Codice fiscale: 16 caratteri';
    if (!legalRepDocumentType) errors.legalRepDocumentType = 'Seleziona tipo documento';
    if (!legalRepDocumentNumber.trim()) errors.legalRepDocumentNumber = 'Numero documento obbligatorio';
    
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    if (!validateProfile()) return;
    setLoading(true);
    try {
      await axios.put(`${API}/business/profile`, {
        company_name: companyName.trim(),
        registered_address: registeredAddress.trim(),
        vat_number: vatNumber.replace(/\s/g, ''),
        iban: normalizeIBAN(iban),
        phone_number: phoneNumber.replace(/[\s\-]/g, ''),
        category,
        legal_rep_first_name: legalRepFirstName.trim(),
        legal_rep_last_name: legalRepLastName.trim(),
        legal_rep_place_of_birth: legalRepPlaceOfBirth.trim(),
        legal_rep_date_of_birth: legalRepDateOfBirth,
        legal_rep_fiscal_code: legalRepFiscalCode.trim().toUpperCase(),
        legal_rep_document_type: legalRepDocumentType,
        legal_rep_document_number: legalRepDocumentNumber.trim()
      }, { withCredentials: true });
      toast.success('Profilo completato!');
      // Refresh user so ProtectedRoute picks up account_status='pending_contract' and routes to contract signing
      await checkAuth();
      navigate('/business/contract', { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore nella compilazione');
    } finally { setLoading(false); }
  };

  const handleSkip = () => {
    navigate('/restaurant/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8" data-testid="restaurant-auth-page">
      <div className="w-full max-w-md">
        <button onClick={() => step === 2 && mode === 'register' ? handleSkip() : navigate('/')} className="flex items-center gap-2 text-sm text-muted-foreground mb-6 hover:text-foreground transition-colors font-sans" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {t('back')}
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <img src="/logo-wordmark@2x.png" alt="Womio" className="h-10 w-auto mx-auto mb-4" data-testid="auth-logo" />
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t('ra_title')}
          </h1>
        </div>

        {/* Progress Indicator */}
        {mode === 'register' && (
          <div className="flex items-center justify-center gap-3 mb-6" data-testid="progress-indicator">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-sans font-medium ${step >= 1 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
              {step > 1 ? <Check className="w-3 h-3" /> : <span>1</span>}
              <span>Account</span>
            </div>
            <div className="w-8 h-px bg-border" />
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-sans font-medium ${step >= 2 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
              <span>2</span>
              <span>Profilo</span>
            </div>
          </div>
        )}

        {/* Step 1: Email + Password */}
        {step === 1 && (
          <Card className="border border-border rounded-2xl">
            <CardContent className="p-6 sm:p-8">
              <p className="text-sm text-muted-foreground font-sans mb-5 text-center">
                {mode === 'register' ? 'Crea il tuo account ristorante' : 'Accedi al tuo account'}
              </p>
              <form onSubmit={handleStep1} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold font-sans flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} /> Email *
                  </Label>
                  <Input data-testid="input-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@tuoristorante.it" className="rounded-lg h-11" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold font-sans flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} /> Password *
                  </Label>
                  <Input data-testid="input-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={mode === 'register' ? 'Min. 8 caratteri' : 'Password'} className="rounded-lg h-11" />
                </div>
                <Button data-testid="submit-step1-btn" type="submit" disabled={loading} className="w-full rounded-xl h-11 btn-lift mt-2">
                  {loading ? '...' : mode === 'register' ? 'Crea account' : 'Accedi'}
                  {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
              <button onClick={() => setMode(mode === 'register' ? 'login' : 'register')} className="w-full text-center text-sm text-primary font-sans mt-5 hover:underline" data-testid="toggle-mode-btn">
                {mode === 'register' ? 'Hai già un account? Accedi' : 'Non hai un account? Registrati'}
              </button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Business Profile */}
        {step === 2 && (
          <Card className="border border-border rounded-2xl">
            <CardContent className="p-6 sm:p-8">
              <p className="text-sm text-muted-foreground font-sans mb-5 text-center">
                {t('cp_title')}
              </p>
              <form onSubmit={handleStep2} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Ragione Sociale *</Label>
                  <Input data-testid="input-company-name" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="es. Trattoria Bella S.r.l." className="rounded-lg h-11" />
                  {profileErrors.companyName && <p className="text-xs text-destructive font-sans">{profileErrors.companyName}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Indirizzo Sede Legale *</Label>
                  <Input data-testid="input-address" value={registeredAddress} onChange={e => setRegisteredAddress(e.target.value)} placeholder="es. Via Roma 42, 20121 Milano (MI)" className="rounded-lg h-11" />
                  {profileErrors.registeredAddress && <p className="text-xs text-destructive font-sans">{profileErrors.registeredAddress}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Partita IVA *</Label>
                  <Input data-testid="input-vat" value={vatNumber} onChange={e => setVatNumber(e.target.value)} placeholder="es. 01234567890" className="rounded-lg h-11" maxLength={13} />
                  {profileErrors.vatNumber && <p className="text-xs text-destructive font-sans">{profileErrors.vatNumber}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">IBAN *</Label>
                  <Input
                    data-testid="input-iban"
                    value={iban}
                    onChange={e => { setIban(normalizeIBAN(e.target.value)); if (ibanStatus.messageKey) setIbanStatus({ valid: null, messageKey: null }); }}
                    onBlur={() => { if (iban) setIbanStatus(validateItalianIBAN(iban)); }}
                    placeholder="es. IT60X0542811101000000123456"
                    className={`rounded-lg h-11 uppercase ${ibanStatus.valid === true ? 'border-emerald-500 focus-visible:ring-emerald-500' : ibanStatus.valid === false ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                    maxLength={34}
                  />
                  {ibanStatus.messageKey === 'iban_valid' && (
                    <p className="text-xs text-emerald-600 font-sans flex items-center gap-1" data-testid="iban-status-valid"><CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> {t('iban_valid')}</p>
                  )}
                  {ibanStatus.messageKey === 'iban_invalid' && (
                    <p className="text-xs text-destructive font-sans flex items-center gap-1" data-testid="iban-status-invalid"><XCircle className="w-3.5 h-3.5" strokeWidth={2} /> {t('iban_invalid')}</p>
                  )}
                  {ibanStatus.messageKey === 'iban_empty' && (
                    <p className="text-xs text-muted-foreground font-sans" data-testid="iban-status-empty">{t('iban_empty')}</p>
                  )}
                  {profileErrors.iban && !ibanStatus.messageKey && <p className="text-xs text-destructive font-sans">{profileErrors.iban}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Telefono *</Label>
                  <Input data-testid="input-phone" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="es. +39 02 1234567" className="rounded-lg h-11" />
                  {profileErrors.phoneNumber && <p className="text-xs text-destructive font-sans">{profileErrors.phoneNumber}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Categoria attività *</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="select-category" className="rounded-lg h-11">
                      <SelectValue placeholder="Seleziona categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_CATEGORIES.map(c => (
                        <SelectItem key={c} value={c} data-testid={`cat-${c}`}>{t(`cat_${c}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {profileErrors.category && <p className="text-xs text-destructive font-sans">{profileErrors.category}</p>}
                </div>

                {/* Legal representative section */}
                <div className="pt-4 mt-4 border-t border-border">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground font-sans mb-3">Legale Rappresentante</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold font-sans">Nome *</Label>
                    <Input data-testid="input-legal-first-name" value={legalRepFirstName} onChange={e => setLegalRepFirstName(e.target.value)} placeholder="Nome" className="rounded-lg h-11" />
                    {profileErrors.legalRepFirstName && <p className="text-xs text-destructive font-sans">{profileErrors.legalRepFirstName}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold font-sans">Cognome *</Label>
                    <Input data-testid="input-legal-last-name" value={legalRepLastName} onChange={e => setLegalRepLastName(e.target.value)} placeholder="Cognome" className="rounded-lg h-11" />
                    {profileErrors.legalRepLastName && <p className="text-xs text-destructive font-sans">{profileErrors.legalRepLastName}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold font-sans">Luogo di nascita *</Label>
                    <Input data-testid="input-legal-birth-place" value={legalRepPlaceOfBirth} onChange={e => setLegalRepPlaceOfBirth(e.target.value)} placeholder="es. Milano" className="rounded-lg h-11" />
                    {profileErrors.legalRepPlaceOfBirth && <p className="text-xs text-destructive font-sans">{profileErrors.legalRepPlaceOfBirth}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold font-sans">Data di nascita *</Label>
                    <Input data-testid="input-legal-birth-date" type="date" value={legalRepDateOfBirth} onChange={e => setLegalRepDateOfBirth(e.target.value)} className="rounded-lg h-11" />
                    {profileErrors.legalRepDateOfBirth && <p className="text-xs text-destructive font-sans">{profileErrors.legalRepDateOfBirth}</p>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold font-sans">Codice Fiscale *</Label>
                  <Input data-testid="input-legal-fiscal-code" value={legalRepFiscalCode} onChange={e => setLegalRepFiscalCode(e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" className="rounded-lg h-11 uppercase" maxLength={16} />
                  {profileErrors.legalRepFiscalCode && <p className="text-xs text-destructive font-sans">{profileErrors.legalRepFiscalCode}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold font-sans">Tipo documento *</Label>
                    <Select value={legalRepDocumentType} onValueChange={setLegalRepDocumentType}>
                      <SelectTrigger data-testid="select-doc-type" className="rounded-lg h-11">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="carta_identita" data-testid="doc-carta-identita">Carta d&apos;identità</SelectItem>
                        <SelectItem value="passaporto" data-testid="doc-passaporto">Passaporto</SelectItem>
                      </SelectContent>
                    </Select>
                    {profileErrors.legalRepDocumentType && <p className="text-xs text-destructive font-sans">{profileErrors.legalRepDocumentType}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold font-sans">Numero documento *</Label>
                    <Input data-testid="input-legal-doc-number" value={legalRepDocumentNumber} onChange={e => setLegalRepDocumentNumber(e.target.value)} placeholder="es. AB1234567" className="rounded-lg h-11" />
                    {profileErrors.legalRepDocumentNumber && <p className="text-xs text-destructive font-sans">{profileErrors.legalRepDocumentNumber}</p>}
                  </div>
                </div>

                <Button data-testid="submit-profile-btn" type="submit" disabled={loading} className="w-full rounded-xl h-11 btn-lift mt-4">
                  {loading ? '...' : 'Completa profilo'}
                  {!loading && <Check className="w-4 h-4 ml-2" />}
                </Button>
              </form>
              <button onClick={handleSkip} className="w-full text-center text-sm text-muted-foreground font-sans mt-4 hover:text-foreground hover:underline" data-testid="skip-btn">
                Completa dopo
              </button>
              <p className="text-[11px] text-muted-foreground font-sans mt-3 text-center leading-relaxed">
                La validazione scontrini e la dashboard finanziaria richiedono il profilo completo.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
